// Pulls today's featured cosmetic from /api/cosmetics/featured and renders a
// dismissible banner in the Lobby. Tap to open the cosmetics modal at the
// correct tab (skin / victory_anim / avatar_frame).
//
// Storage: dismissed banner is suppressed for the rest of the day via
// localStorage keyed by the featured date. Reappears next UTC day.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../lib/api";

const DISMISSED_KEY = "quattro_daily_deal_dismissed_for";

function getDismissedDate() {
  try { return localStorage.getItem(DISMISSED_KEY) || ""; } catch { return ""; }
}
function setDismissedDate(date) {
  try { localStorage.setItem(DISMISSED_KEY, date); } catch {}
}

const KIND_TO_TAB = {
  skin: "skins",
  victory_anim: "victory_anims",
  avatar_frame: "avatar_frames",
};

export default function DailyDealBanner({ onOpen }) {
  const [feat, setFeat] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/cosmetics/featured`)
      .then((r) => { if (alive) setFeat(r.data?.featured || null); })
      .catch(() => { /* offline / unavailable — silently hide banner */ });
    return () => { alive = false; };
  }, []);

  // Suppress when user already dismissed for today's featured date
  const todaysDate = feat?.date || "";
  const isDismissedForToday = todaysDate && getDismissedDate() === todaysDate;

  if (!feat || dismissed || isDismissedForToday) return null;

  const handleOpen = () => {
    onOpen?.(KIND_TO_TAB[feat.kind] || "skins");
  };
  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissedDate(todaysDate);
    setDismissed(true);
  };

  const savings = feat.original_price_gems - feat.discounted_price_gems;

  return (
    <AnimatePresence>
      <motion.button
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        onClick={handleOpen}
        className="w-full rounded-2xl px-4 py-3 mb-4 md:mb-6 flex items-center gap-3 text-left relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(255,42,107,0.18), rgba(181,55,242,0.18))",
          border: "1px solid rgba(255,80,140,0.45)",
          boxShadow: "0 0 24px rgba(255,80,140,0.18)",
        }}
        data-testid="daily-deal-banner"
      >
        {/* Subtle moving sheen */}
        <motion.span
          className="absolute inset-y-0"
          style={{
            width: 60,
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)",
            pointerEvents: "none",
          }}
          initial={{ x: "-30%" }}
          animate={{ x: "150%" }}
          transition={{ duration: 3.6, repeat: Infinity, ease: "linear" }}
        />
        <span
          className="font-display font-black flex-shrink-0 rounded-full"
          style={{
            fontSize: 10, letterSpacing: "0.12em",
            background: "linear-gradient(135deg, #FF2A6B, #B537F2)",
            color: "#fff", padding: "5px 9px",
            boxShadow: "0 0 12px rgba(255,80,140,0.5)",
          }}
          data-testid="daily-deal-badge"
        >
          DAILY −{feat.discount_pct}%
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm truncate">
            <span style={{ color: "#FF6BAA" }}>{feat.name}</span>
            <span className="text-white/55"> · today only</span>
          </div>
          <div className="text-[11px] text-white/65 truncate">
            ◈ <span className="line-through opacity-60">{feat.original_price_gems}</span>
            {"  "}
            <b style={{ color: "#FF6BAA" }}>◈ {feat.discounted_price_gems}</b>
            <span className="text-white/55"> · save {savings} gems</span>
          </div>
        </div>
        <span
          onClick={handleDismiss}
          role="button"
          tabIndex={0}
          className="text-white/55 hover:text-white text-xl leading-none px-2 flex-shrink-0"
          aria-label="Dismiss"
          data-testid="daily-deal-dismiss"
        >×</span>
      </motion.button>
    </AnimatePresence>
  );
}
